import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, ArrowLeft, Sparkles, Zap, Rocket, CreditCard, Apple, RefreshCw, Smartphone, Wallet } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { User } from "@supabase/supabase-js";
import {
  trackPricingPageViewed,
  trackPlanClicked,
  trackUpgradeInitiated,
  trackPotentialLeadLogged,
} from "@/lib/analytics";
import { logPaymentEvent } from "@/lib/payment-analytics";
import { isNativePlatform, getPlatform } from "@/lib/platform";
import { openPaymentWebView } from "@/lib/in-app-browser";
import {
  shouldUseAppleIAP,
  getAppleProducts,
  purchaseAppleProduct,
  verifyAppleTransaction,
  restoreApplePurchases,
  APPLE_PRODUCT_IDS,
  type AppleProduct,
} from "@/lib/apple-iap";
import { Seo } from "@/components/Seo";

type PackId = "free" | "refill" | "boost";

interface PackPrice {
  amount: number;
  display: string;
}

interface UserLocation {
  country: string;
  countryCode: string;
  region: string;
  city: string | null;
  ip: string | null;
  pricing: {
    currency: string;
    symbol: string;
    refill: PackPrice;
    boost: PackPrice;
  };
  products?: {
    refill: { name: string; description: string };
    boost: { name: string; description: string };
  };
}

const PACK_CREDITS: Record<"refill" | "boost", number> = { refill: 20, boost: 50 };

// Compare-at (reference) prices shown struck-through on pricing cards.
// Display-only: the shown discount is computed from these vs the live price
// and resolves to 40% in both currencies (₹330→₹199, ₹665→₹399, $6.65→$3.99, $13.30→$7.99).
const COMPARE_AT_PRICES: Record<"refill" | "boost", Partial<Record<"INR" | "USD", number>>> = {
  refill: { INR: 500, USD: 6.65 },
  boost: { INR: 800, USD: 13.3 },
};


const PAID_FEATURES = (credits: number) => [
  `${credits} credits (one-time purchase)`,
  "1 credit per variation",
  "Up to 3 generations in one go",
  "Credits never expire unless used",
  "60-day auto-save retention",
  "90+ Design Styles in 14 Categories",
  "Watermark-free downloads",
  "Priority support",
];

const FREE_FEATURES = [
  "3 trial credits (one-time only)",
  "1 credit per variation",
  "7-day auto-save retention",
  "Limited design styles",
  "Watermarked downloads",
  "Basic support",
];

// Strip markdown artifacts (**, trailing backslashes, escaped newlines) from
// product descriptions coming from the payment provider dashboard.
const cleanFeature = (line: string) =>
  line
    .replace(/\\n/g, " ")
    .replace(/\*\*/g, "")
    .replace(/\\+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const parseFeatures = (desc?: string): string[] | undefined => {
  if (!desc) return undefined;
  const lines = desc.split("\n").map(cleanFeature).filter(Boolean);
  return lines.length ? lines : undefined;
};

const paidFeatures = (desc: string | undefined, credits: number): string[] => {
  const providerFeatures = parseFeatures(desc);
  if (!providerFeatures) return PAID_FEATURES(credits);

  const providerBenefits = providerFeatures.filter(
    (feature) =>
      !/\b\d+\s+credits?\b/i.test(feature) &&
      !/^one-time purchase$/i.test(feature) &&
      !/up to\s+3\s+generations?\s+in\s+one\s+go/i.test(feature) &&
      !/credits?.*never expire/i.test(feature),
  );

  return [
    `${credits} credits (one-time purchase)`,
    "Up to 3 generations in one go",
    "Credits never expire unless used",
    ...providerBenefits,
  ];
};

const Pricing = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [currentTier, setCurrentTier] = useState<"free" | "pro" | "business" | "enterprise">("free");
  const [planName, setPlanName] = useState<string | null>(null);
  const [designsThisMonth, setDesignsThisMonth] = useState(0);
  const [bonusCredits, setBonusCredits] = useState(0);
  const [loading, setLoading] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [loadingLocation, setLoadingLocation] = useState(true);
  const [isAppleIAP, setIsAppleIAP] = useState(false);
  const [appleProducts, setAppleProducts] = useState<AppleProduct[]>([]);
  const [isRestoringPurchases, setIsRestoringPurchases] = useState(false);

  useEffect(() => {
    // Check if we should use Apple IAP
    const useApple = shouldUseAppleIAP();
    setIsAppleIAP(useApple);

    if (useApple) {
      // Fetch Apple products with localized pricing
      getAppleProducts().then((products) => {
        console.log("[Pricing] Apple products loaded:", products);
        setAppleProducts(products);
        setLoadingLocation(false);
      });
    } else {
      // Use Dodo location-based pricing
      detectUserLocation();
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setUser(session.user);
        loadUserProfile(session.user.id);
      }
    });
  }, []);

  useEffect(() => {
    // Track pricing page view when data is loaded
    if (!loadingLocation && currentTier) {
      trackPricingPageViewed(currentTier, designsThisMonth, bonusCredits);
      logPaymentEvent("pricing_viewed", {
        from_tier: currentTier,
        region: userLocation?.region,
        country_code: userLocation?.country,
      });
    }
  }, [loadingLocation, currentTier, designsThisMonth, bonusCredits]);

  const detectUserLocation = async () => {
    try {
      console.log("Detecting user location...");
      const { data, error } = await supabase.functions.invoke("detect-user-location");

      if (error) throw error;

      setUserLocation(data);
    } catch (error) {
      console.error("Location detection failed, using default USD pricing:", error);
      // Fallback to USD
      setUserLocation({
        country: "Unknown",
        countryCode: "US",
        region: "NA",
        city: null,
        ip: null,
        pricing: {
          currency: "USD",
          symbol: "$",
          refill: { amount: 3.99, display: "$3.99" },
          boost: { amount: 7.99, display: "$7.99" },
        },
      });
    } finally {
      setLoadingLocation(false);
    }
  };

  const loadUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("subscription_tier, designs_this_month, bonus_generations, plan_name")
        .eq("id", userId)
        .single();

      if (error) throw error;
      if (data) {
        setCurrentTier(data.subscription_tier);
        setDesignsThisMonth(data.designs_this_month || 0);
        setBonusCredits(data.bonus_generations || 0);
        setPlanName(data.plan_name ?? null);
      }
    } catch (error: any) {
      console.error("Error loading profile:", error);
    }
  };

  const tiers = useMemo(() => {
    const freeTier = {
      id: "free" as PackId,
      name: "Free",
      price: isAppleIAP ? "Free" : `${userLocation?.pricing.symbol ?? "$"}0`,
      priceAmount: 0,
      currency: userLocation?.pricing.currency ?? "USD",
      credits: 3,
      popular: false,
      cta: "Your starting point",
      features: FREE_FEATURES,
      refPriceDisplay: null as string | null,
      discountPercent: null as number | null,
    };

    // For Apple IAP, use Apple product pricing
    if (isAppleIAP) {
      const refillProduct = appleProducts.find((p) => p.id === APPLE_PRODUCT_IDS.refill);
      const boostProduct = appleProducts.find((p) => p.id === APPLE_PRODUCT_IDS.boost);

      return [
        freeTier,
        {
          id: "refill" as PackId,
          name: "Refill",
          price: refillProduct?.price || "Loading...",
          priceAmount: refillProduct?.priceAmount || 0,
          currency: refillProduct?.currency || "USD",
          credits: PACK_CREDITS.refill,
          popular: false,
          cta: "Buy Refill",
          features: PAID_FEATURES(PACK_CREDITS.refill),
          refPriceDisplay: null as string | null,
          discountPercent: null as number | null,
        },
        {
          id: "boost" as PackId,
          name: "Boost",
          price: boostProduct?.price || "Loading...",
          priceAmount: boostProduct?.priceAmount || 0,
          currency: boostProduct?.currency || "USD",
          credits: PACK_CREDITS.boost,
          popular: true,
          cta: "Buy Boost",
          features: PAID_FEATURES(PACK_CREDITS.boost),
          refPriceDisplay: null as string | null,
          discountPercent: null as number | null,
        },
      ];
    }

    // For Dodo Payments, use location-based pricing
    if (!userLocation?.pricing) return [];

    const pricing = userLocation.pricing;
    const packCurrency: "INR" | "USD" = pricing.currency === "INR" ? "INR" : "USD";

    // Compare-at (reference) prices used only for the discount display.
    // Chosen so the displayed discount resolves to a true 40% in both currencies.
    const discountInfo = (
      pack: "refill" | "boost",
      amount: number,
    ): { refPriceDisplay: string | null; discountPercent: number | null } => {
      const refPrice = COMPARE_AT_PRICES[pack][packCurrency];
      if (!refPrice || amount <= 0 || refPrice <= amount) {
        return { refPriceDisplay: null, discountPercent: null };
      }
      const symbol = pricing.symbol ?? "$";
      const refPriceDisplay =
        packCurrency === "INR" ? `${symbol}${refPrice}` : `${symbol}${refPrice.toFixed(2)}`;
      return {
        refPriceDisplay,
        discountPercent: pack === "boost" ? 50 : 40,
      };
    };

    const refillDiscount = discountInfo("refill", pricing.refill.amount);
    const boostDiscount = discountInfo("boost", pricing.boost.amount);

    return [
      freeTier,
      {
        id: "refill" as PackId,
        name: userLocation.products?.refill.name ?? "Refill",
        price: pricing.refill.display,
        priceAmount: pricing.refill.amount,
        currency: pricing.currency,
        credits: PACK_CREDITS.refill,
        popular: false,
        cta: "Buy Refill",
          features: paidFeatures(userLocation.products?.refill.description, PACK_CREDITS.refill),
        refPriceDisplay: refillDiscount.refPriceDisplay,
        discountPercent: refillDiscount.discountPercent,
      },
      {
        id: "boost" as PackId,
        name: userLocation.products?.boost.name ?? "Boost",
        price: pricing.boost.display,
        priceAmount: pricing.boost.amount,
        currency: pricing.currency,
        credits: PACK_CREDITS.boost,
        popular: true,
        cta: "Buy Boost",
          features: paidFeatures(userLocation.products?.boost.description, PACK_CREDITS.boost),
        refPriceDisplay: boostDiscount.refPriceDisplay,
        discountPercent: boostDiscount.discountPercent,
      },
    ];
  }, [userLocation, isAppleIAP, appleProducts]);


  const logPotentialLead = async (tier: (typeof tiers)[0]) => {
    try {
      console.log("Logging potential lead for pack:", tier.id);

      await supabase.functions.invoke("log-potential-lead", {
        body: {
          userId: user?.id || null,
          email: user?.email || null,
          planInterested: tier.id,
          detectedRegion: userLocation?.region || "unknown",
          detectedCountry: userLocation?.country || "unknown",
          ipAddress: userLocation?.ip || null,
          currencyShown: tier.currency,
          priceShown: tier.price,
          userTier: currentTier,
          userAgent: navigator.userAgent,
        },
      });

      trackPotentialLeadLogged(tier.id, !!user, userLocation?.region || "unknown", userLocation?.country || "unknown");

      console.log("Lead logged successfully");
    } catch (error) {
      console.error("Failed to log lead:", error);
      // Don't block the UI if logging fails
    }
  };

  const handleBuy = async (tier: (typeof tiers)[0]) => {
    if (tier.id === "free") return;

    // Log the potential lead FIRST
    await logPotentialLead(tier);

    if (!user) {
      toast.error("Please sign in to buy credits");
      navigate("/auth");
      return;
    }

    setLoading(tier.id);
    try {
      // Apple IAP flow for iOS
      if (isAppleIAP) {
        const productId = tier.id === "refill" ? APPLE_PRODUCT_IDS.refill : APPLE_PRODUCT_IDS.boost;
        console.log("[Pricing] Starting Apple IAP purchase for:", productId);

        const purchaseResult = await purchaseAppleProduct(productId);

        if (!purchaseResult.success) {
          if (purchaseResult.error === "Purchase cancelled") {
            toast.info("Purchase cancelled");
          } else {
            toast.error(purchaseResult.error || "Purchase failed");
          }
          setLoading(null);
          return;
        }

        // Verify the transaction with our backend
        if (purchaseResult.transactionId) {
          console.log("[Pricing] Verifying transaction:", purchaseResult.transactionId);
          const verifyResult = await verifyAppleTransaction(purchaseResult.transactionId);

          if (verifyResult.success) {
            toast.success(`${verifyResult.credits ?? tier.credits} credits added to your account.`);
            navigate("/settings?fromPayment=true");
          } else {
            toast.error(verifyResult.error || "Verification failed");
          }
        }

        setLoading(null);
        return;
      }

      // Dodo Payments flow for Android/Web
      const isNative = isNativePlatform();
      const platform = getPlatform();
      const useNativeWebView = isNative || platform !== "web";
      console.log(
        `[Payment] Platform: ${platform}, isNative: ${isNative}, useNativeWebView: ${useNativeWebView}, user: ${user.id}`,
      );

      console.log("[Payment] Calling create-dodo-payment...");
      const { data, error } = await supabase.functions.invoke("create-dodo-payment", {
        body: {
          pack: tier.id,
          userId: user.id,
          userEmail: user.email,
          userName: user.user_metadata?.full_name,
          billingCurrency: userLocation?.pricing?.currency || "USD",
          isMobile: useNativeWebView,
        },
      });
      console.log("[Payment] Response:", { data, error });

      if (error) throw error;

      if (data?.checkoutUrl) {
        if (useNativeWebView) {
          // Open in WebView on mobile (native or Capacitor-detected)
          console.log("[Payment] Opening in-app WebView...");
          await openPaymentWebView(data.checkoutUrl, {
            onSuccess: () => {
              console.log("Payment successful!");
              setLoading(null);
              toast.success("Payment successful! Adding your credits...");
              navigate("/settings?fromPayment=true");
            },
            onFailure: (error) => {
              console.log("Payment failed:", error);
              setLoading(null);
              toast.error(error || "Payment was not completed");
            },
            onClose: () => {
              console.log("User closed payment browser");
              setLoading(null);
              // User may have completed payment before closing
              navigate("/settings?fromPayment=true");
            },
          });
        } else {
          // Regular redirect for web
          window.location.href = data.checkoutUrl;
        }
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error: unknown) {
      console.error("Purchase error:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to initiate payment";
      toast.error(errorMessage);
      setLoading(null);
    }
  };

  const handleRestorePurchases = async () => {
    if (!isAppleIAP) return;

    setIsRestoringPurchases(true);
    try {
      const result = await restoreApplePurchases();

      if (result.success && result.restoredProducts.length > 0) {
        toast.success(`Restored ${result.restoredProducts.length} purchase(s)`);
        // Refresh user profile to get updated credits
        if (user) {
          loadUserProfile(user.id);
        }
      } else if (result.success) {
        toast.info("No restorable purchases found");
      } else {
        toast.error(result.error || "Failed to restore purchases");
      }
    } catch (error) {
      console.error("Restore error:", error);
      toast.error("Failed to restore purchases");
    } finally {
      setIsRestoringPurchases(false);
    }
  };

  const getTierIcon = (tierId: string) => {
    switch (tierId) {
      case "free":
        return Sparkles;
      case "refill":
        return Zap;
      case "boost":
        return Rocket;
      default:
        return Sparkles;
    }
  };

  const PricingCardSkeleton = () => (
    <Card className="relative p-6 shadow-card">
      <div className="flex flex-col items-center mb-6">
        <Skeleton className="w-16 h-16 rounded-full mb-4" />
        <Skeleton className="h-7 w-28 mb-3" />
        <Skeleton className="h-9 w-20 mb-2" />
        <Skeleton className="h-4 w-40" />
      </div>
      <div className="space-y-3 mb-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2">
            <Skeleton className="w-5 h-5 rounded-full flex-shrink-0" />
            <Skeleton className="h-4 flex-1" />
          </div>
        ))}
      </div>
      <Skeleton className="h-10 w-full rounded-md" />
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      <Seo
        title="Decor Canvas Pricing — One-Time AI Room Design Credit Packs"
        description="Buy credits once, use them anytime. Refill (20 credits) and Boost (50 credits) one-time packs for Decor Canvas AI interior design. No subscription; credits never expire unless used."
        canonicalPath="/pricing"
      />
      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/90 backdrop-blur-sm border-b border-border safe-area-inset-top">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => navigate("/designer")} size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Back to Designer</span>
            <span className="sm:hidden">Back</span>
          </Button>
          {user && (
            <div className="text-xs sm:text-sm text-muted-foreground pl-2 sm:pl-0">
              {planName && currentTier !== "free" && (
                <>
                  <Badge variant="secondary" className="text-xs">
                    {planName}
                  </Badge>{" "}
                  •{" "}
                </>
              )}
              {bonusCredits} credits available
            </div>
          )}
        </div>
      </header>

      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-12 max-w-7xl">
        {/* Title */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold leading-[1.2] pb-1 mb-3 sm:mb-4 bg-gradient-hero bg-clip-text text-transparent">
            Buy Credits, Not Subscriptions
          </h1>
          <p className="text-sm sm:text-lg text-muted-foreground max-w-2xl mx-auto px-2">
            Pay once and design at your own pace. Credits are added straight to your balance and never expire unless used.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {loadingLocation
            ? Array.from({ length: 3 }).map((_, i) => <PricingCardSkeleton key={i} />)
            : tiers.map((tier) => {
                const Icon = getTierIcon(tier.id);
                const isFree = tier.id === "free";

                return (
                  <Card
                    key={tier.id}
                    className={`relative p-6 shadow-card hover:shadow-elegant transition-smooth ${
                      tier.popular ? "border-2 border-primary" : ""
                    }`}
                  >
                    {tier.popular && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">
                        Most Popular
                      </Badge>
                    )}

                    <div className="text-center mb-6">
                      <div className="bg-gradient-card rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <Icon className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="text-2xl font-bold mb-2 flex flex-wrap items-center justify-center gap-2">
                        {tier.name}
                        {tier.discountPercent !== null && (
                          <Badge className="bg-green-600 text-white text-xs font-bold px-2 py-1">
                            {tier.discountPercent}% OFF
                          </Badge>
                        )}
                      </h3>
                      {tier.refPriceDisplay && (
                        <div className="text-base text-muted-foreground/70 line-through decoration-primary decoration-2">
                          {tier.refPriceDisplay}
                        </div>
                      )}
                      <div className="flex flex-wrap items-baseline justify-center gap-2 mb-2">
                        <span className="text-3xl font-bold text-primary">{tier.price}</span>
                        {!isFree && (
                          <span className="text-sm text-muted-foreground">
                            {isAppleIAP ? "one-time · tax included" : "one-time payment + tax"}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {isFree ? "3 trial credits" : `${tier.credits} credits • never expire unless used`}
                      </p>
                    </div>

                    <div className="space-y-3 mb-6">
                      {tier.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button
                      onClick={() => {
                        if (isFree) return;
                        trackPlanClicked(
                          tier.name,
                          tier.priceAmount,
                          currentTier,
                          tier.currency,
                          userLocation?.region || "unknown",
                        );
                        logPaymentEvent("plan_clicked", {
                          plan_id: tier.id,
                          plan_name: tier.name,
                          price_amount: tier.priceAmount,
                          currency: tier.currency,
                          from_tier: currentTier,
                          to_tier: tier.id,
                          provider: isAppleIAP ? "apple_iap" : "dodo",
                          region: userLocation?.region,
                          country_code: userLocation?.country,
                        });
                        trackUpgradeInitiated(currentTier, tier.id, tier.priceAmount, tier.currency);
                        logPaymentEvent("checkout_initiated", {
                          plan_id: tier.id,
                          plan_name: tier.name,
                          price_amount: tier.priceAmount,
                          currency: tier.currency,
                          from_tier: currentTier,
                          to_tier: tier.id,
                          provider: isAppleIAP ? "apple_iap" : "dodo",
                          region: userLocation?.region,
                          country_code: userLocation?.country,
                        });
                        handleBuy(tier);
                      }}
                      disabled={isFree || loading === tier.id}
                      variant={tier.popular ? "default" : "outline"}
                      className="w-full"
                    >
                      {loading === tier.id ? "Processing..." : tier.cta}
                    </Button>
                  </Card>
                );
              })}
        </div>

        {/* Payment Methods */}
        <Card className="p-4 sm:p-8 shadow-card mb-8 sm:mb-12">
          <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center">Secure Payment Methods</h3>
          <div className="flex flex-wrap justify-center items-center gap-8">
            {isAppleIAP ? (
              <>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Apple className="w-6 h-6" />
                  <span>Apple Pay</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CreditCard className="w-6 h-6" />
                  <span>Credit/Debit Cards</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CreditCard className="w-6 h-6" />
                  <span>Cards (Credit/Debit)</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Smartphone className="w-6 h-6" />
                  <span>UPI</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Wallet className="w-6 h-6" />
                  <span>Digital Wallets (GPay, Apple Pay, etc.)</span>
                </div>
              </>
            )}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-6">
            {isAppleIAP
              ? "Purchases are processed securely through the App Store and charged once to your Apple ID account (taxes included). Credit packs are one-time purchases — nothing renews."
              : "We support all major payment methods available in your country, including cards, UPI, wallets, and more. All transactions are encrypted and include applicable taxes. Your payment information is never stored on our servers."}
          </p>

          {/* Restore Purchases button for iOS */}
          {isAppleIAP && (
            <div className="flex justify-center mt-6">
              <Button variant="outline" onClick={handleRestorePurchases} disabled={isRestoringPurchases}>
                <RefreshCw className={`w-4 h-4 mr-2 ${isRestoringPurchases ? "animate-spin" : ""}`} />
                {isRestoringPurchases ? "Restoring..." : "Restore Purchases"}
              </Button>
            </div>
          )}
        </Card>

        {/* Apple-required purchase disclosure (iOS only) */}
        {isAppleIAP && (
          <Card className="p-6 shadow-card mb-12 text-sm text-muted-foreground space-y-3">
            <h4 className="font-semibold text-foreground">Purchase Terms</h4>
            <ul className="list-disc pl-5 space-y-1">
              <li>Payment will be charged to your Apple ID account at confirmation of purchase.</li>
              <li>Refill and Boost are one-time credit packs. There is no subscription and nothing renews.</li>
              <li>Credits are added to your balance immediately after purchase and never expire unless used.</li>
              <li>Credit packs are consumable, so they are not restorable once used.</li>
            </ul>
            <div className="flex gap-4 pt-2">
              <a href="/privacy" className="text-primary underline">
                Privacy Policy
              </a>
              <a href="/terms" className="text-primary underline">
                Terms of Service
              </a>
            </div>
          </Card>
        )}

        {/* Comparison Table */}
        <Card className="p-4 sm:p-8 shadow-card">
          <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center">Detailed Comparison</h3>
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="w-full text-xs sm:text-sm md:text-base">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 sm:py-4 px-1 sm:px-2">Feature</th>
                  {tiers.map((tier) => (
                    <th key={tier.id} className="text-center py-2 sm:py-4 px-1 sm:px-2">
                      {tier.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="py-2 sm:py-4 px-1 sm:px-2">Credits Included</td>
                  {tiers.map((tier) => (
                    <td key={tier.id} className="text-center py-2 sm:py-4 px-1 sm:px-2">
                      {tier.credits}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2 sm:py-4 px-1 sm:px-2">Credit Cost per Variation</td>
                  {tiers.map((tier) => (
                    <td key={tier.id} className="text-center py-2 sm:py-4 px-1 sm:px-2">
                      1
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2 sm:py-4 px-1 sm:px-2">Generations at Once</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">1</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">Up to 3</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">Up to 3</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2 sm:py-4 px-1 sm:px-2">Auto-Save Retention</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">7 days</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">60 days</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">60 days</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2 sm:py-4 px-1 sm:px-2">Design Styles</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">Limited</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">90+ in 14 categories</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">90+ in 14 categories</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2 sm:py-4 px-1 sm:px-2">Watermark-free</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">✗</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">✓</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">✓</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2 sm:py-4 px-1 sm:px-2">Support</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">Basic</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">Priority</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">Priority</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2 sm:py-4 px-1 sm:px-2">Payment</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">—</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">One-time</td>
                  <td className="text-center py-2 sm:py-4 px-1 sm:px-2">One-time</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Pricing;
